import { useCallback, useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AnimatePresence, motion, type PanInfo } from 'framer-motion';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';
import { fetchGalleryImageUrl, type GalleryPhoto } from './api';

interface GalleryLightboxProps {
  albumId: string;
  photos: GalleryPhoto[];
  index: number;
  onClose: () => void;
  onIndexChange: (index: number) => void;
}

/**
 * Full-screen photo viewer with swipe/drag navigation and full-res URLs.
 */
export function GalleryLightbox({
  albumId,
  photos,
  index,
  onClose,
  onIndexChange,
}: GalleryLightboxProps): JSX.Element {
  const photo = photos[index];
  const [touchStartX, setTouchStartX] = useState<number | null>(null);

  const urlQuery = useQuery({
    queryKey: ['gallery-image-url', albumId, photo?.photo_id],
    queryFn: () => fetchGalleryImageUrl(albumId, photo!.photo_id),
    enabled: !!photo?.photo_id,
    staleTime: 5 * 60_000,
  });

  const displayUrl = urlQuery.data || photo?.thumbnail_url || photo?.image_url;

  const goPrev = useCallback(() => {
    if (photos.length === 0) return;
    onIndexChange((index - 1 + photos.length) % photos.length);
  }, [index, onIndexChange, photos.length]);

  const goNext = useCallback(() => {
    if (photos.length === 0) return;
    onIndexChange((index + 1) % photos.length);
  }, [index, onIndexChange, photos.length]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') goPrev();
      if (e.key === 'ArrowRight') goNext();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, goPrev, goNext]);

  const onDragEnd = (_: unknown, info: PanInfo) => {
    if (info.offset.x < -72 || info.velocity.x < -400) goNext();
    else if (info.offset.x > 72 || info.velocity.x > 400) goPrev();
  };

  const onTouchStart = (e: React.TouchEvent) => {
    setTouchStartX(e.touches[0]?.clientX ?? null);
  };

  const onTouchEnd = (e: React.TouchEvent) => {
    if (touchStartX == null) return;
    const diff = touchStartX - (e.changedTouches[0]?.clientX ?? touchStartX);
    if (Math.abs(diff) > 48) {
      if (diff > 0) goNext();
      else goPrev();
    }
    setTouchStartX(null);
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
      className="fixed inset-0 z-[200] flex flex-col bg-black"
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
    >
      <header className="flex shrink-0 items-center justify-between px-4 pb-2 pt-safe">
        <span className="text-caption text-white/70">
          {index + 1} / {photos.length}
        </span>
        <button
          type="button"
          aria-label="Close"
          onClick={onClose}
          className="flex h-10 w-10 items-center justify-center rounded-pill bg-white/10 text-white hover:bg-white/20"
        >
          <X className="h-5 w-5" strokeWidth={1.5} />
        </button>
      </header>

      <div className="relative flex flex-1 items-center justify-center overflow-hidden">
        <button
          type="button"
          aria-label="Previous photo"
          onClick={goPrev}
          className="absolute left-0 z-10 flex h-full w-14 items-center justify-center text-white/50 hover:text-white"
        >
          <ChevronLeft className="h-8 w-8" strokeWidth={1.75} />
        </button>

        <AnimatePresence mode="wait" initial={false}>
          <motion.div
            key={photo?.photo_id ?? index}
            drag="x"
            dragConstraints={{ left: 0, right: 0 }}
            dragElastic={0.12}
            onDragEnd={onDragEnd}
            initial={{ opacity: 0, x: 24 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -24 }}
            transition={{ duration: 0.2 }}
            className="flex max-h-full max-w-full items-center justify-center px-16"
          >
            {urlQuery.isLoading && !displayUrl ? (
              <div className="h-64 w-64 animate-pulse rounded-xl bg-white/10" />
            ) : displayUrl ? (
              <img
                src={displayUrl}
                alt={photo?.caption ?? ''}
                className="max-h-[min(72dvh,100%)] max-w-full select-none object-contain"
                draggable={false}
              />
            ) : (
              <p className="text-body-sm text-white/60">Unable to load image</p>
            )}
          </motion.div>
        </AnimatePresence>

        <button
          type="button"
          aria-label="Next photo"
          onClick={goNext}
          className="absolute right-0 z-10 flex h-full w-14 items-center justify-center text-white/50 hover:text-white"
        >
          <ChevronRight className="h-8 w-8" strokeWidth={1.75} />
        </button>
      </div>

      {photo?.caption ? (
        <p className="shrink-0 truncate px-6 py-3 pb-safe-or-3 text-center text-body-sm text-white/75">
          {photo.caption}
        </p>
      ) : (
        <div className="pb-safe-or-3" />
      )}
    </motion.div>
  );
}
